import { API_URL } from "@/6-api/apiUrl";

let initialized = false;

function initImageProtection() {
  if (initialized) return;
  initialized = true;

  const apiOrigin = (() => {
    try {
      return new URL(String(API_URL)).origin;
    } catch {
      return "";
    }
  })();

  const BLANK = "data:image/gif;base64,R0lGODlhAQABAAAAACw=";
  const cache = new Map<string, string>();
  const pending = new Map<string, Promise<string>>();

  function asAbsolute(url: string): string {
    try {
      return new URL(url, location.href).href;
    } catch {
      return url;
    }
  }

  function toApi(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;
    return `${API_URL}${path.startsWith("/") ? "" : "/"}${path}`;
  }

  function isApi(url: string): boolean {
    try {
      return new URL(url, location.href).origin === apiOrigin;
    } catch {
      return false;
    }
  }

  const srcDesc = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, "src")!;
  const origSrcSet = (img: HTMLImageElement, val: string) =>
    srcDesc.set!.call(img, val);
  // Capture the native setAttribute BEFORE we override the prototype below.
  // Reading it off the prototype at call-time would resolve to our own patched
  // version once installed, an infinite setAttribute → origSetAttr loop that
  // blanks the page on client navigation (React sets img src via setAttribute).
  // eslint-disable-next-line @typescript-eslint/unbound-method -- captured native ref, always invoked via .call(el, …)
  const nativeSetAttr = HTMLImageElement.prototype.setAttribute;
  const origSetAttr = (el: HTMLImageElement, name: string, value: string) =>
    nativeSetAttr.call(el, name, value);

  function setSrc(img: HTMLImageElement, val: string) {
    origSrcSet(img, val);
  }

  async function fetchBlob(url: string): Promise<string> {
    if (cache.has(url)) return cache.get(url)!;
    if (pending.has(url)) return pending.get(url)!;

    const p = (async () => {
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`${resp.status}`);

        let blob: Blob;
        if ((resp.headers.get("content-type") || "").includes("application/json")) {
          // The file endpoint points at the stored file. Same-origin storage
          // (/media) can be blob-fetched; a cross-origin store (e.g. a presigned
          // S3 link) can't (no CORS), so hand the <img> that URL directly, which
          // needs no CORS. Presigned links expire, so this branch is not cached.
          const json = (await resp.json()) as { url: string };
          const resolved = toApi(json.url);
          if (!isApi(resolved)) return resolved;
          const r2 = await fetch(resolved);
          if (!r2.ok) throw new Error(`${r2.status}`);
          blob = await r2.blob();
        } else {
          blob = await resp.blob();
        }

        const blobUrl = URL.createObjectURL(blob);
        cache.set(url, blobUrl);
        return blobUrl;
      } finally {
        pending.delete(url);
      }
    })();

    pending.set(url, p);
    return p;
  }

  function protect(img: HTMLImageElement, apiUrl: string) {
    img.dataset._origSrc = apiUrl;

    if (cache.has(apiUrl)) {
      setSrc(img, cache.get(apiUrl)!);
      return;
    }
    setSrc(img, BLANK);
    fetchBlob(apiUrl)
      .then((b) => {
        if (img.isConnected && img.dataset._origSrc === apiUrl) setSrc(img, b);
      })
      .catch(() => {});
  }

  function intercept(val: string, img: HTMLImageElement): boolean {
    if (!val || val.startsWith("blob:") || val === BLANK || val.startsWith("data:")) return false;
    const abs = asAbsolute(val);
    if (!isApi(abs)) return false;
    if (cache.has(abs)) {
      setSrc(img, cache.get(abs)!);
    } else {
      protect(img, abs);
    }
    return true;
  }

  Object.defineProperty(HTMLImageElement.prototype, "src", {
    ...srcDesc,
    set(this: HTMLImageElement, val: string) {
      if (!intercept(val, this)) origSrcSet(this, val);
    },
  });

  HTMLImageElement.prototype.setAttribute = function (this: HTMLImageElement, name: string, value: string) {
    if (name === "src" && intercept(value, this)) return;
    origSetAttr(this, name, value);
  };

  function scan(el: Element) {
    if (el.tagName !== "IMG") return;
    const raw = el.getAttribute("src") || "";
    if (!raw || raw === BLANK || raw.startsWith("blob:") || raw.startsWith("data:")) return;
    const a = asAbsolute(raw);
    if (isApi(a)) protect(el as unknown as HTMLImageElement, a);
  }

  const moOpts: MutationObserverInit = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["src"],
  };

  const mo = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "childList") {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          const el = n as Element;
          if (el.tagName === "IMG") scan(el);
          el.querySelectorAll?.("img").forEach(scan);
        });
      } else if (m.type === "attributes") {
        scan(m.target as Element);
      }
    }
  });

  mo.observe(document.documentElement, moOpts);

  // Capture the native attachShadow BEFORE overriding the prototype. Reading it
  // off the prototype at call-time would resolve to our own patched version once
  // installed, an infinite attachShadow → origAttach recursion (same trap as
  // setAttribute above). Blog posts render into a shadow root, which triggers it.
  // eslint-disable-next-line @typescript-eslint/unbound-method -- captured native ref, always invoked via .call(el, …)
  const nativeAttachShadow = Element.prototype.attachShadow;
  Element.prototype.attachShadow = function (init: ShadowRootInit) {
    const shadow = nativeAttachShadow.call(this, init);
    mo.observe(shadow, moOpts);
    return shadow;
  };

  document.querySelectorAll("img").forEach(scan);
  document.querySelectorAll("*").forEach((el) => {
    if (el.shadowRoot) {
      mo.observe(el.shadowRoot, moOpts);
      el.shadowRoot.querySelectorAll("img").forEach(scan);
    }
  });

  // Presigned S3 links expire seconds after they are minted (the backend's
  // `querystring_expire`), but a lazy <img> only fetches once scrolled near the
  // viewport, so a link resolved at mount can 403 by then. On a failed load,
  // mint a fresh link once. The BLANK placeholder's error is always swallowed;
  // a repeat failure reaches the page's onError, so <Image> shows its fallback
  // instead of a placeholder that pulses forever.
  document.addEventListener(
    "error",
    (e) => {
      const img = e.target;
      if (!(img instanceof HTMLImageElement)) return;
      const apiUrl = img.dataset._origSrc;
      if (!apiUrl) return;
      if (img.getAttribute("src") !== BLANK) {
        if (img.dataset._retried === apiUrl) return;
        img.dataset._retried = apiUrl;
        protect(img, apiUrl);
      }
      e.stopImmediatePropagation();
    },
    true,
  );

  window.addEventListener("pagehide", () => {
    for (const url of cache.values()) URL.revokeObjectURL(url);
    cache.clear();
    pending.clear();
  });
}

if (typeof window !== "undefined") initImageProtection();
